<template>
  <div :key="refreshKey" class="flex flex-col gap-5 relative">
    <!-- Floating Cart Toggle Button -->
    <button
      @click="toggleCart"
      class="fixed top-1/2 transform -translate-y-1/2 z-[60] bg-600 transition-all duration-300 md:hidden flex items-center gap-2 cart-toggle bg-main-600 dark:bg-sub-800 text-white p-3 rounded-lg shadow-lg hover:bg-main-700 dark:hover:bg-sub-900"
      :class="{ '-left-[4px]': cartOpen, '-right-[4px]': !cartOpen }"
    >
      <i class="fa-solid fa-shopping-cart"></i>
      <span
        class="bg-white text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold"
        :class="{ 'animate-giggle': shouldGiggle }"
        @animationend="shouldGiggle = false"
      >
        {{ cart.length > 9 ? "9+" : cart.length }}
      </span>
    </button>

    <!-- Glass Morphism Cart Panel -->
    <transition name="slide-fade">
      <div v-if="cartOpen" class="fixed inset-0 z-50 pointer-events-none">
        <!-- Overlay Background -->
        <div
          @click="cartOpen = false"
          class="cart-backdrop absolute inset-0 bg-black bg-opacity-30 backdrop-blur-sm pointer-events-auto"
        ></div>

        <!-- Cart Panel -->
        <div
          class="cart-panel absolute right-0 top-0 h-full bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-l border-white/20 dark:border-gray-700 shadow-2xl overflow-y-auto pointer-events-auto"
        >
          <!-- Your existing cart content goes here -->
          <div
            class="sticky top-0 z-10 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md p-6 pb-4 border-b border-gray-100 dark:border-gray-800"
          >
            <div class="flex justify-between items-center">
              <h2 class="text-2xl font-bold text-gray-900 dark:text-white">
                Your Cart
                <span
                  class="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2"
                >
                  ({{ cart.length }} {{ cart.length === 1 ? "item" : "items" }})
                </span>
              </h2>
              <button
                @click="clearCart"
                class="text-red-500 hover:text-red-600 dark:hover:text-red-400 transition-colors p-2 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20"
                :disabled="cart.length === 0"
              >
                <i class="fa-solid fa-trash-can text-lg"></i>
              </button>
            </div>
          </div>

          <!-- Cart Items - Premium Scroll Area -->
          <div class="flex-1 overflow-y-auto p-6 pt-6 custom-scrollbar">
            <div
              v-if="cart.length === 0"
              class="flex flex-col items-center justify-center h-full"
            >
              <div class="relative w-32 h-32 mb-6">
                <div
                  class="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-50 dark:from-gray-900 dark:to-gray-800 rounded-full animate-pulse"
                ></div>
                <i
                  class="fa-solid fa-basket-shopping text-4xl text-gray-300 dark:text-gray-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                ></i>
              </div>
              <p class="text-lg font-medium text-gray-700 dark:text-gray-300">
                Your cart feels light
              </p>
              <p
                class="text-sm text-gray-400 dark:text-gray-500 mt-2 max-w-xs text-center"
              >
                Add some products to get started
              </p>
            </div>

            <div v-else class="space-y-4">
              <div
                v-for="item in cart"
                :key="item.id"
                class="group relative flex items-start gap-4 p-1 rounded-2xl transition-all duration-300 hover:shadow-sm hover:bg-gray-50/50 dark:hover:bg-gray-800/70"
              >
                <!-- Item Image -->
                <div
                  class="relative flex-shrink-0 w-16 h-20 rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800"
                >
                  <img
                    :src="getFileUrl(item.image)"
                    @error="
                      (event) => (event.target.src = '/placeholder-item.svg')
                    "
                    class="absolute inset-0 w-full h-full object-cover object-top"
                    alt=""
                  />
                  <div
                    class="absolute bottom-0 text-sm -left-2 bg-white dark:bg-gray-900 rounded-full text-white shadow-sm border border-gray-200 dark:border-gray-700"
                  >
                    <div class="flex items-center px-2 py-1">
                      <button
                        @click.stop="decrementItem(item)"
                        class="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      >
                        <i class="fa-solid fa-minus text-[10px]"></i>
                      </button>
                      <span
                        class="text-xs font-medium mx-1 min-w-[18px] text-center"
                        >{{ item.quantity }}</span
                      >
                      <button
                        @click.stop="incrementItem(item)"
                        class="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      >
                        <i class="fa-solid fa-plus text-[10px]"></i>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Item Details -->
                <div class="flex-grow text-sm">
                  <div class="flex justify-between items-start">
                    <h3
                      class="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2 pr-4"
                    >
                      {{ item.name }}
                    </h3>
                    <button
                      @click.stop="removeFromCart(item.id)"
                      class="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors p-1 -mt-1 -mr-1"
                    >
                      <i class="fa-solid fa-xmark text-sm"></i>
                    </button>
                  </div>
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {{ getCurrencySymbol(storeCurrencyId)
                    }}{{ Number(item.cost)?.toFixed(2) }} each
                  </p>

                  <div class="flex justify-between items-center mt-1">
                    <div class="flex items-center gap-1">
                      <span
                        class="text-xs font-medium py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                      >
                        {{ item.variant || "Total" }}
                      </span>
                    </div>
                    <p
                      class="text-sm font-medium text-gray-800 dark:text-gray-200"
                    >
                      {{ getCurrencySymbol(storeCurrencyId)
                      }}{{ (Number(item.cost) * item.quantity)?.toFixed(2) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Sticky Summary Footer -->
          <div
            class="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-500 p-6 pt-4 shadow-[0_-4px_12px_rgba(0,0,0,0.02)] dark:shadow-[0_-4px_12px_rgba(0,0,0,0.1)]"
          >
            <!-- Summary Breakdown -->
            <div class="space-y-3 mb-4">
              <div class="flex justify-between text-sm">
                <span class="text-gray-600 dark:text-gray-400">Subtotal</span>
                <span class="font-medium text-800 dark:text-gray-200"
                  >{{ getCurrencySymbol(storeCurrencyId)
                  }}{{ subtotal?.toFixed(2) }}</span
                >
              </div>
              <div class="flex justify-between text-sm">
                <span class="text-gray-600 dark:text-gray-400">Tax (10%)</span>
                <span class="font-medium text-800 dark:text-gray-200"
                  >{{ getCurrencySymbol(storeCurrencyId)
                  }}{{ tax?.toFixed(2) }}</span
                >
              </div>
            </div>

            <!-- Total -->
            <div
              class="flex justify-between items-center py-3 border-y border-gray-100 dark:border-gray-700 my-3"
            >
              <span class="font-bold text-gray-900 dark:text-white">Total</span>
              <span class="text-xl font-bold text-blue-600 dark:text-blue-400">
                {{ getCurrencySymbol(storeCurrencyId) }}{{ total?.toFixed(2) }}
              </span>
            </div>

            <!-- Payment Tabs -->
            <div class="mb-4">
              <div
                class="flex justify-between border-b border-gray-200 dark:border-gray-700"
              >
                <button
                  v-for="method in paymentMethods"
                  :key="method.name"
                  @click="selectedPayment = method.name"
                  class="px-4 py-2 text-sm font-medium relative"
                  :class="
                    selectedPayment === method.name
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  "
                >
                  {{ method.name }}
                  <span
                    v-if="selectedPayment === method.name"
                    class="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 dark:bg-blue-400 rounded-t-full"
                  ></span>
                </button>
              </div>
            </div>

            <!-- Checkout Button -->
            <button
              @click="completeSale"
              :disabled="cart.length === 0"
              class="w-full py-4 px-6 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white rounded-xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              <i class="fa-solid fa-lock text-sm"></i>
              <span>Checkout</span>
              <span class="font-normal opacity-90"
                >• {{ getCurrencySymbol(storeCurrencyId)
                }}{{ total?.toFixed(2) }}</span
              >
            </button>

            <!-- Trust Badges -->
            <div class="flex justify-center items-center gap-4 mt-4 opacity-70">
              <i class="fa-solid fa-shield-check text-xs text-green-500"></i>
              <span class="text-xs text-gray-500 dark:text-gray-400"
                >SSL Secure</span
              >
              <span class="text-xs text-gray-500 dark:text-gray-400">•</span>
              <i class="fa-regular fa-credit-card text-xs text-blue-500"></i>
              <span class="text-xs text-gray-500 dark:text-gray-400"
                >Encrypted</span
              >
            </div>
          </div>

          <!-- Rest of your cart items, summary, etc. -->
        </div>
      </div>
    </transition>

    <!-- Header Section -->
    <div
      class="relative flex flex-col w-full h-min text-main-700 bg-white rounded-xl bg-clip-border p-6 gap-5 dark:bg-gray-800 dark:text-gray-200 shadow-sm border border-gray-100 dark:border-gray-700"
    >
      <!-- Navigation -->
      <nav
        class="w-full flex flex-row portrait:flex-col justify-between portrait:gap-5"
      >
        <!-- Back Button and Title -->
        <div
          class="w-full max-w-fit flex flex-row gap-5 items-center justify-start"
        >
          <button
            @click="goback"
            class="p-3 flex flex-col items-center justify-center aspect-square w-fit bg-gray-100 hover:bg-gray-200 rounded-xl dark:bg-gray-700 dark:hover:bg-gray-600 transition-all duration-300"
          >
            <i class="fa-solid fa-arrow-left text-base dark:text-gray-200"></i>
          </button>
          <div>
            <h3 class="text-2xl font-bold dark:text-gray-200">
              Point of Sale System
            </h3>
            <p
              class="block text-sm font-normal text-gray-500 dark:text-gray-400 mt-1"
            >
              Streamline your sales with our modern POS system
            </p>
          </div>
        </div>

        <button
          @click="showTutorial"
          class="w-full landscape:max-w-fit flex gap-2 items-center justify-center px-5 py-4 bg-blue-gray-500/10 rounded-lg text-[14px] border-blue-gray-600/10 font-medium h-min dark:bg-gray-700 dark:hover:bg-gray-600 dark:border-gray-600 dark:text-gray-200 transition-all duration-300 ease-in-out transform hover:scale-105 hover:shadow-lg hover:bg-blue-gray-500/30"
        >
          <i
            class="fa-regular fa-circle-play dark:text-gray-200 transition-transform duration-300 ease-in-out transform hover:rotate-12"
          ></i>
          <span>Watch Video Tutorial</span>
        </button>
      </nav>

      <!-- Stats Cards -->
      <div class="flex flex-wrap w-full gap-3">
        <div class="min-w-[150px] flex-1">
          <div
            class="w-full p-4 rounded-xl cursor-pointer h-full flex justify-between items-center transition-all duration-300 bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/30"
          >
            <div>
              <h3 class="font-bold text-2xl dark:text-gray-100">
                {{ instock }}
              </h3>
              <p
                class="text-xs font-medium mt-1 text-green-700 dark:text-green-300"
              >
                Products in stock
              </p>
            </div>
            <div
              class="w-10 h-10 flex justify-center items-center rounded-lg bg-green-100 dark:bg-green-800/30"
            >
              <i
                class="fa-solid fa-boxes-stacked text-lg text-green-600 dark:text-green-400"
              ></i>
            </div>
          </div>
        </div>

        <div class="min-w-[150px] flex-1">
          <div
            class="w-full p-4 rounded-xl cursor-pointer h-full flex justify-between items-center transition-all duration-300 bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/20 dark:hover:bg-amber-900/30"
          >
            <div>
              <h3 class="font-bold text-2xl dark:text-gray-100">
                {{ outofstock }}
              </h3>
              <p
                class="text-xs font-medium mt-1 text-amber-700 dark:text-amber-300"
              >
                Products out of stock
              </p>
            </div>
            <div
              class="w-10 h-10 flex justify-center items-center rounded-lg bg-amber-100 dark:bg-amber-800/30"
            >
              <i
                class="fa-solid fa-box-open text-lg text-amber-600 dark:text-amber-400"
              ></i>
            </div>
          </div>
        </div>

        <div class="min-w-[150px] flex-1">
          <div
            class="w-full p-4 rounded-xl cursor-pointer h-full flex justify-between items-center transition-all duration-300 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30"
          >
            <div>
              <h3 class="font-bold text-2xl dark:text-gray-100">
                {{ sold }}
              </h3>
              <p
                class="text-xs font-medium mt-1 text-blue-700 dark:text-blue-300"
              >
                Products sold
              </p>
            </div>
            <div
              class="w-10 h-10 flex justify-center items-center rounded-lg bg-blue-100 dark:bg-blue-800/30"
            >
              <i
                class="fa-solid fa-tags text-lg text-blue-600 dark:text-blue-400"
              ></i>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main POS Layout -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Product List -->
      <div
        class="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6"
      >
        <div class="flex justify-between portrait:gap-6 items-center mb-6">
          <h2 class="text-xl font-bold text-gray-900 dark:text-gray-200">
            Products
          </h2>
          <div class="relative w-64">
            <input
              v-model="productSearchQuery"
              type="text"
              placeholder="Search products..."
              class="w-full pl-10 pr-4 py-2 rounded-lg bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
            <i
              class="fa-solid fa-search absolute left-3 top-3 text-gray-400 dark:text-gray-300"
            ></i>
          </div>
        </div>

        <!-- Product Grid -->

        <div
          class="w-full h-fit flex flex-col"
          :class="{
            'items-center justify-center': loading,
            'items-start justify-start': !loading,
          }"
        >
          <div
            v-if="!loading && filteredProducts.length > 0"
            class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 w-full"
          >
            <div
              v-for="product in filteredProducts"
              :key="product.id"
              class="product-card-container group relative bg-white dark:bg-gray-800 rounded-lg p-3 cursor-pointer hover:shadow-lg transition-all duration-300 border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col"
              @click="addToCart(product)"
              :class="{ 'animate-add-to-cart': recentlyAdded === product.id }"
              @animationend="recentlyAdded = null"
            >
              <transition name="fade-up">
                <div
                  v-if="recentlyAdded === product.id"
                  class="absolute top-2 right-2 dark:bg-sub-900 bg-main-800 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold z-20"
                >
                  +1
                </div>
              </transition>
              <!-- Sold Out Badge (more prominent) -->
              <div
                v-if="product.stock_quantity === 0"
                class="absolute top-3 right-3 bg-red-600 text-white text-xs font-semibold px-2.5 py-1 rounded-md shadow-sm z-10"
              >
                Sold Out
              </div>

              <!-- Product Image with subtle overlay when sold out -->
              <div
                class="relative w-full h-40 bg-gray-100 dark:bg-gray-700 rounded-lg mb-3 overflow-hidden"
                :class="{ 'opacity-80': product.stock_quantity === 0 }"
              >
                <img
                  :src="getFileUrl(product.image)"
                  @error="
                    (event) => (event.target.src = '/landscape-placeholder.svg')
                  "
                  :alt="product.name"
                  class="h-full w-full object-cover object-top"
                />
                <div
                  v-if="product.stock_quantity === 0"
                  class="absolute inset-0 bg-black bg-opacity-20"
                ></div>
              </div>

              <!-- Product Info -->
              <div class="flex-grow flex flex-col">
                <p
                  class="text-sm font-semibold text-gray-900 dark:text-gray-100 line-clamp-2 mb-1"
                >
                  {{ product.name }}
                </p>

                <!-- Price - more emphasis -->
                <div class="mt-auto">
                  <p
                    class="text-lg font-medium text-main-600 dark:text-sub-800"
                  >
                    {{ getCurrencySymbol(storeCurrencyId)
                    }}{{ Number(product?.cost).toFixed(2) }}
                  </p>

                  <!-- Stock information - more contextual -->
                  <div v-if="product.stock_quantity > 0" class="mt-1.5">
                    <div class="flex items-center">
                      <div
                        class="flex-grow h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden"
                        :class="{
                          'animate-pulse-stock': recentlyAdded === product.id,
                        }"
                        :title="`${product.stock_quantity} in stock`"
                      >
                        <div
                          class="h-full rounded-full"
                          :class="{
                            'bg-green-500': product.stock_quantity >= 10,
                            'bg-amber-500':
                              product.stock_quantity < 10 &&
                              product.stock_quantity >= 5,
                            'bg-red-500': product.stock_quantity < 5,
                          }"
                          :style="{
                            width: `${Math.min(100, (product.stock_quantity / 20) * 100)}%`,
                          }"
                        ></div>
                      </div>
                      <span
                        class="ml-2 text-xs font-medium"
                        :class="{
                          'text-green-600 dark:text-green-400':
                            product.stock_quantity >= 10,
                          'text-amber-600 dark:text-amber-400':
                            product.stock_quantity < 10 &&
                            product.stock_quantity >= 5,
                          'text-red-600 dark:text-red-400':
                            product.stock_quantity < 5,
                        }"
                      >
                        {{ product.stock_quantity }} left
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <template v-else>
            <div
              v-if="!loading && filteredProducts.length === 0"
              class="flex flex-col items-center justify-center p-8 w-full"
            >
              <div class="text-center max-w-md">
                <!-- Icon -->
                <div
                  class="mx-auto mb-4 text-gray-400 dark:text-gray-500 w-full flex flex-col items-center"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-16 w-16"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="1.5"
                      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                    />
                  </svg>
                </div>

                <!-- Title -->
                <h3
                  class="text-lg font-medium text-gray-700 dark:text-gray-200 mb-2"
                >
                  No Products Found
                </h3>

                <!-- Message -->
                <p class="text-gray-500 dark:text-gray-400 mb-6">
                  We couldn't find any products matching your criteria.
                </p>

                <!-- Action Buttons -->
                <div class="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    @click="resetFilters"
                    class="px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center bg-main-600 dark:bg-sub-800 text-white hover:bg-main-700 dark:hover:bg-sub-900"
                  >
                    <i class="fas fa-filter-circle-xmark mr-2"></i>
                    Reset Filters
                  </button>
                  <button
                    @click="openAddProductModal"
                    class="px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-700"
                  >
                    <i class="fas fa-plus-circle mr-2"></i>
                    Add New Product
                  </button>
                </div>
              </div>
            </div>
            <div
              v-else
              class="w-full flex flex-col justify-center items-center h-20 mt-6 mb-16"
            >
              <LoadingComponent /></div
          ></template>
        </div>
      </div>

      <!-- Premium Cart & Checkout Section -->
      <div
        class="bg-white dark:bg-gray-800 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-800 overflow-hidden relative hidden md:flex flex-col h-[calc(100vh-200px)] min-h-[600px] max-h-[800px]"
      >
        <!-- Floating Header -->
        <div
          class="sticky top-0 z-10 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md p-6 pb-4 border-b border-gray-100 dark:border-gray-800"
        >
          <div class="flex justify-between items-center">
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white">
              Your Cart
              <span
                class="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2"
              >
                ({{ cart.length }} {{ cart.length === 1 ? "item" : "items" }})
              </span>
            </h2>
            <button
              @click="clearCart"
              class="text-red-500 hover:text-red-600 dark:hover:text-red-400 transition-colors p-2 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20"
              :disabled="cart.length === 0"
            >
              <i class="fa-solid fa-trash-can text-lg"></i>
            </button>
          </div>
        </div>

        <!-- Cart Items - Premium Scroll Area -->
        <div class="flex-1 overflow-y-auto p-6 pt-0 custom-scrollbar">
          <div
            v-if="cart.length === 0"
            class="flex flex-col items-center justify-center h-full"
          >
            <div class="relative w-32 h-32 mb-6">
              <div
                class="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-50 dark:from-gray-900 dark:to-gray-800 rounded-full animate-pulse"
              ></div>
              <i
                class="fa-solid fa-basket-shopping text-4xl text-gray-300 dark:text-gray-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
              ></i>
            </div>
            <p class="text-lg font-medium text-gray-700 dark:text-gray-300">
              Your cart feels light
            </p>
            <p
              class="text-sm text-gray-400 dark:text-gray-500 mt-2 max-w-xs text-center"
            >
              Add some products to get started
            </p>
          </div>

          <div v-else class="space-y-4">
            <div
              v-for="item in cart"
              :key="item.id"
              class="group relative flex items-start gap-4 p-1 rounded-2xl transition-all duration-300 hover:shadow-sm hover:bg-gray-50/50 dark:hover:bg-gray-800/70"
            >
              <!-- Item Image -->
              <div
                class="relative flex-shrink-0 w-16 h-20 rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800"
              >
                <img
                  :src="getFileUrl(item.image)"
                  @error="
                    (event) => (event.target.src = '/placeholder-item.svg')
                  "
                  class="absolute inset-0 w-full h-full object-cover object-top"
                  alt=""
                />
                <div
                  class="absolute bottom-0 text-sm -left-2 bg-white dark:bg-gray-900 rounded-full text-white shadow-sm border border-gray-200 dark:border-gray-700"
                >
                  <div class="flex items-center px-2 py-1">
                    <button
                      @click.stop="decrementItem(item)"
                      class="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <i class="fa-solid fa-minus text-[10px]"></i>
                    </button>
                    <span
                      class="text-xs font-medium mx-1 min-w-[18px] text-center"
                      >{{ item.quantity }}</span
                    >
                    <button
                      @click.stop="incrementItem(item)"
                      class="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <i class="fa-solid fa-plus text-[10px]"></i>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Item Details -->
              <div class="flex-grow text-sm">
                <div class="flex justify-between items-start">
                  <h3
                    class="text-sm font-semibold text-gray-900 dark:text-white line-clamp-2 pr-4"
                  >
                    {{ item.name }}
                  </h3>
                  <button
                    @click.stop="removeFromCart(item.id)"
                    class="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors p-1 -mt-1 -mr-1"
                  >
                    <i class="fa-solid fa-xmark text-sm"></i>
                  </button>
                </div>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {{ getCurrencySymbol(storeCurrencyId)
                  }}{{ Number(item.cost)?.toFixed(2) }} each
                </p>

                <div class="flex justify-between items-center mt-1">
                  <div class="flex items-center gap-1">
                    <span
                      class="text-xs font-medium py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                    >
                      {{ item.variant || "Total" }}
                    </span>
                  </div>
                  <p
                    class="text-sm font-medium text-gray-800 dark:text-gray-200"
                  >
                    {{ getCurrencySymbol(storeCurrencyId)
                    }}{{ (Number(item.cost) * item.quantity)?.toFixed(2) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Sticky Summary Footer -->
        <div
          class="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-500 p-6 pt-4 shadow-[0_-4px_12px_rgba(0,0,0,0.02)] dark:shadow-[0_-4px_12px_rgba(0,0,0,0.1)]"
        >
          <!-- Summary Breakdown -->
          <div class="space-y-3 mb-4">
            <div class="flex justify-between text-sm">
              <span class="text-gray-600 dark:text-gray-400">Subtotal</span>
              <span class="font-medium text-800 dark:text-gray-200"
                >{{ getCurrencySymbol(storeCurrencyId)
                }}{{ subtotal?.toFixed(2) }}</span
              >
            </div>
            <div class="flex justify-between text-sm">
              <span class="text-gray-600 dark:text-gray-400">Tax (10%)</span>
              <span class="font-medium text-800 dark:text-gray-200"
                >{{ getCurrencySymbol(storeCurrencyId)
                }}{{ tax?.toFixed(2) }}</span
              >
            </div>
          </div>

          <!-- Total -->
          <div
            class="flex justify-between items-center py-3 border-y border-gray-100 dark:border-gray-700 my-3"
          >
            <span class="font-bold text-gray-900 dark:text-white">Total</span>
            <span class="text-xl font-bold text-blue-600 dark:text-blue-400">
              {{ getCurrencySymbol(storeCurrencyId) }}{{ total?.toFixed(2) }}
            </span>
          </div>

          <!-- Payment Tabs -->
          <div class="mb-4">
            <div
              class="flex justify-between border-b border-gray-200 dark:border-gray-700"
            >
              <button
                v-for="method in paymentMethods"
                :key="method.name"
                @click="selectedPayment = method.name"
                class="px-4 py-2 text-sm font-medium relative"
                :class="
                  selectedPayment === method.name
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                "
              >
                {{ method.name }}
                <span
                  v-if="selectedPayment === method.name"
                  class="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 dark:bg-blue-400 rounded-t-full"
                ></span>
              </button>
            </div>
          </div>

          <!-- Checkout Button -->
          <button
            @click="completeSale"
            :disabled="cart.length === 0"
            class="w-full py-4 px-6 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white rounded-xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <!-- Fixed width container for stable layout -->
            <div class="flex items-center gap-3 min-w-[120px] justify-center">
              <i class="fa-solid fa-lock text-sm flex-shrink-0"></i>
              <span class="flex-shrink-0">Checkout</span>
              <!-- Price with stable positioning -->
              <span
                class="font-normal opacity-90 min-w-[60px] text-left transition-all duration-100 text-nowrap"
                :class="{
                  'opacity-0': cart.length === 0,
                  'opacity-90': cart.length > 0,
                }"
              >
                • {{ getCurrencySymbol(storeCurrencyId)
                }}{{ total?.toFixed(2) }}
              </span>
            </div>
          </button>

          <!-- Trust Badges -->
          <div class="flex justify-center items-center gap-4 mt-4 opacity-70">
            <i class="fa-solid fa-shield-check text-xs text-green-500"></i>
            <span class="text-xs text-gray-500 dark:text-gray-400"
              >SSL Secure</span
            >
            <span class="text-xs text-gray-500 dark:text-gray-400">•</span>
            <i class="fa-regular fa-credit-card text-xs text-blue-500"></i>
            <span class="text-xs text-gray-500 dark:text-gray-400"
              >Encrypted</span
            >
          </div>
        </div>
      </div>
    </div>
  </div>
  <ReceiptModal
    :show-receipt-modal="showReceipt"
    :receipt-items="cart"
    :receipt-subtotal="subtotal"
    :receipt-tax="tax"
    :receipt-total="total"
    :selected-payment-method="selectedPayment"
    :store-name="storeName"
    :order-code="orderCode"
    :currency-symbol="getCurrencySymbol(storeCurrencyId)"
    @close="
      cart = [];
      showReceipt = false;
      orderCode = '';
    "
  />
</template>

<script>
import { mapActions } from "vuex";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import ReceiptModal from "@/components/receiptModal.vue";

export default {
  data() {
    return {
      isFullscreen: false,
      cartOpen: false,
      recentlyAdded: null,
      refreshKey: this.$store.state.storeUpdatedAt || Date.now(),
      productSearchQuery: "",
      currencies: [],
      selectedPayment: "Cash",
      loading: false,
      showReceipt: false,
      shouldGiggle: false,
      orderCode: "",
      paymentMethods: [
        { name: "Cash", icon: "fa-solid fa-money-bill-wave" },
        { name: "Card", icon: "fa-solid fa-credit-card" },
        { name: "Transfer", icon: "fa-solid fa-mobile-screen" },
      ],
      products: [],
      cart: [],
      summary: {
        totalInStock: 0,
        totalOutOfStock: 0,
      },
    };
  },
  inject: ["getFileUrl"],
  components: {
    ReceiptModal,
  },
  computed: {
    storeCurrencyId() {
      return this.$store.state.selectedStore.default_currency;
    },
    storeName() {
      return this.$store.state.selectedStore?.store_name;
    },
    instock() {
      return this.summary.totalInStock || 0;
    },
    outofstock() {
      return this.summary.totalOutOfStock || 0;
    },
    sold() {
      return this.summary.totalSold || 0;
    },
    filteredProducts() {
      const query = this.productSearchQuery.toLowerCase();

      // Filter by name based on search query
      const filtered = this.products.filter(
        (product) =>
          product.visibility === 1 &&
          product.name.toLowerCase().includes(query.toLowerCase())
      );

      // Sort the filtered products
      return filtered.sort((a, b) => {
        if (a.visibility !== b.visibility) {
          return b.visibility - a.visibility;
        }

        // Finally, by updatedAt (most recently updated first)
        return new Date(b.updatedAt) - new Date(a.updatedAt);
      });
    },
    subtotal() {
      return this.cart.reduce(
        (sum, item) => sum + Number(item.cost) * item.quantity,
        0
      );
    },
    tax() {
      return this.subtotal * 0.1; // 10% tax
    },
    total() {
      return this.subtotal + this.tax;
    },
  },
  mounted() {
    this.fetchProducts();
    this.getCurrency();
    if (typeof window !== "undefined") {
      // For SSR compatibility
      this.loadSound();
    }
  },
  beforeMount() {
    if (this.chaChingSound) {
      this.chaChingSound.pause();
      this.chaChingSound = null;
    }
  },
  methods: {
    ...mapActions(["getProducts", "fetchCurrency"]),
    goback() {
      this.$router.back();
    },
    loadSound() {
      // Create audio programmatically
      this.chaChingSound = new Audio(require("@/assets/sounds/cashin.ogg"));
      this.chaChingSound.preload = "auto";
      this.chaChingSound.volume = 0.3; // Lower volume for better UX
    },
    triggerGiggle() {
      this.shouldGiggle = true;
    },
    playChaChing() {
      if (!this.chaChingSound) return;

      try {
        this.chaChingSound.currentTime = 0; // Rewind
        this.chaChingSound.play().catch((e) => {
          console.warn("Sound playback blocked:", e);
          // Optionally show a muted icon when sound is blocked
        });
      } catch (e) {
        console.error("Sound error:", e);
      }
    },
    toggleCart() {
      this.cartOpen = !this.cartOpen;
    },
    showTutorial() {
      toast.info("Tutorial coming soon!");
    },
    addToCart(product) {
      if (product.stock_quantity === 0) return;

      // Reset any existing animation
      this.recentlyAdded = null;

      // Force DOM update before triggering animation
      this.$nextTick(() => {
        this.recentlyAdded = product.id;

        const existingItem = this.cart.find((item) => item.id === product.id);
        if (existingItem) {
          if (product.stock_quantity <= existingItem.quantity) {
            toast.error("Cannot add more than available stock");
            return;
          }
          existingItem.quantity++;
        } else {
          this.cart.push({ ...product, quantity: 1 });
        }

        this.playChaChing();

        // Auto-reset animation after 1s as fallback
        setTimeout(() => {
          this.recentlyAdded = null;
        }, 1000);
      });
    },
    getCurrencySymbol(id) {
      if (id) {
        const currency = this.currencies.find((currency) => currency.id === id);
        return currency ? currency.currency_symbol : null;
      }
    },
    getCurrencyCode(id) {
      if (id) {
        const currency = this.currencies.find((currency) => currency.id === id);
        return currency ? currency.currency_code : null;
      }
    },
    async getCurrency() {
      const response = await this.fetchCurrency();
      if (response.success) {
        this.currencies = response.currency;
      }
    },
    async fetchProducts() {
      try {
        this.loading = true;
        const response = await this.getProducts();
        if (response.success) {
          this.products = response.products;
          this.summary = response.summary;
        } else {
          toast.error(response.error || "Failed to fetch products");
        }
      } catch (error) {
        toast.error("Error fetching products");
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    removeFromCart(id) {
      this.cart = this.cart.filter((item) => item.id !== id);
    },
    incrementItem(item) {
      item.quantity++;
    },
    decrementItem(item) {
      if (item.quantity > 1) {
        item.quantity--;
      } else {
        this.removeFromCart(item.id);
      }
    },
    clearCart() {
      this.cart = [];
    },
    completeSale() {
      if (this.cart.length === 0) {
        toast.error("Your cart is empty");
        return;
      }
      this.orderCode =
        this.storeName?.charAt(0).toUpperCase() +
        "POS" +
        Math.floor(Math.random() * 1000000000 + 1);
      this.showReceipt = true;
      toast.success(
        `Payment processed for $${this.total?.toFixed(2)} via ${this.selectedPayment}`
      );
    },
  },
  watch: {
    "cart.length": {
      handler(newVal, oldVal) {
        if (newVal > oldVal) {
          this.triggerGiggle();
        }
        this.lastCartLength = newVal;
      },
      immediate: true,
    },
    "$store.state.storeUpdatedAt": {
      immediate: true,
      async handler(val) {
        this.refreshKey = val;
        await this.fetchProducts();
        await this.getCurrency();
      },
    },
  },
};
</script>

<style scoped>
/* Remove or modify the global transition-all to be more specific */
.transition-all:not(.cart-toggle):not(.cart-panel) {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .cart-collapsed {
    transform: translateX(100%);
  }
}

@keyframes pop {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.3);
  }
  100% {
    transform: scale(1);
  }
}

.animate-giggle {
  animation: pop 0.3s cubic-bezier(0.5, 1.5, 0.5, 1);
}

@keyframes addToCart {
  0% {
    transform: scale(1);
  }
  25% {
    transform: scale(0.95);
  }
  50% {
    transform: scale(1.05) rotate(1deg);
  }
  75% {
    transform: scale(0.98);
  }
  100% {
    transform: scale(1);
  }
}

.animate-add-to-cart {
  animation: addToCart 0.4s ease-out;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
}

/* Add this to make the image "pop" */
.animate-add-to-cart .relative img {
  transform: scale(1.03);
  transition: transform 0.3s ease;
}

/* Keep your slide-fade animations - these are correct */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.slide-fade-enter-from {
  opacity: 0;
}

.slide-fade-leave-to {
  opacity: 0;
}

/* Cart panel slide animation */
.slide-fade-enter-from .cart-panel {
  transform: translateX(100%);
  opacity: 0;
}

.slide-fade-enter-active .cart-panel {
  transition: all 0.6s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.slide-fade-leave-to .cart-panel {
  transform: translateX(100%);
  opacity: 0;
}

.slide-fade-leave-active .cart-panel {
  transition: all 0.4s cubic-bezier(0.55, 0.055, 0.675, 0.19);
}

/* Backdrop fade animation */
.slide-fade-enter-from .cart-backdrop {
  opacity: 0;
}

.slide-fade-enter-active .cart-backdrop {
  transition: opacity 0.4s ease-out;
}

.slide-fade-leave-to .cart-backdrop {
  opacity: 0;
}

.slide-fade-leave-active .cart-backdrop {
  transition: opacity 0.3s ease-in;
}

/* Fix button timing to match cart panel */
.cart-toggle {
  transition: all 0.6s cubic-bezier(0.165, 0.84, 0.44, 1);
}

/* Consolidate scrollbar styles - remove duplicates */
::-webkit-scrollbar,
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track,
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb,
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover,
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

/* Dark mode scrollbar */
.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

.fade-up-enter-active {
  transition: all 0.5s ease;
}
.fade-up-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}
.fade-up-enter-from,
.fade-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

@keyframes pulseStock {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.animate-pulse-stock {
  animation: pulseStock 1s ease 2;
}

/* Mobile responsive width for cart */
@media (max-width: 768px) {
  .cart-panel {
    width: 100% !important;
  }
}

@media (min-width: 769px) {
  .cart-panel {
    width: 400px !important;
    max-width: 90vw !important;
  }
}

/* Icon transitions */
.fa-maximize,
.fa-minimize {
  transition: transform 0.3s ease;
}
</style>
